import { isEmpty } from "lodash-es";
import { Alert, Button, DeleteIcon, Space } from "melco-ui";
import { useTranslation } from "react-i18next";
import { Key } from "antd/lib/table/interface";
import { useDeleteDesigns } from "../../../hooks/libraries/designs/useDeleteDesigns";
import { SharedserviceMelcoModelsDesignListItem, useConfirm } from "melco-shared-logic";
import { useShowWarningForRowKeys } from "../../../hooks/table/useShowWarningForRowKeys";

type BulkActionDeleteDesignsProps<T> = {
  selectedRowKeys: Key[];
  setSelectedRowKeys: (keys: Key[]) => void;
  refetch?: () => Promise<any>;
  data?: T[];
};

export const BulkActionDeleteDesigns: React.FC<
  BulkActionDeleteDesignsProps<SharedserviceMelcoModelsDesignListItem>
> = ({ selectedRowKeys, setSelectedRowKeys, refetch, data }) => {
  const { t } = useTranslation();

  const deleteDesigns = useDeleteDesigns();
  const showWarningForRowKeys = useShowWarningForRowKeys(selectedRowKeys, data);

  const confirmDelete = useConfirm(
    async () => {
      const successfullyDeletedIds = await deleteDesigns(
        selectedRowKeys.map((key) => key.toString())
      );

      if (refetch) {
        refetch();
      }

      // keep items that were not deleted selected
      setSelectedRowKeys(
        selectedRowKeys.filter(
          (id) => !successfullyDeletedIds.includes(id.toString())
        )
      );
    },
    {
      translationPrefix: "designs.actions.delete",
      translationOptions: {
        count: selectedRowKeys.length,
      },
      content: showWarningForRowKeys.length && (
        <Space direction="vertical" size="small">
          {t("designs.actions.delete.confirmation.message", {
            count: selectedRowKeys.length,
          })}
          <Alert
            type="warning"
            withBackground
            description={t("designs.actions.delete.confirmation.warning", {
              count: showWarningForRowKeys.length,
            })}
            showIcon={false}
          />
        </Space>
      ),
    }
  );

  if (isEmpty(selectedRowKeys)) {
    return null;
  }

  return (
    <Button
      key="bulk-delete"
      icon={<DeleteIcon />}
      onClick={() => {
        confirmDelete();
      }}
    >
      {t("designs.actions.delete.button")}
    </Button>
  );
};
